import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Row,
  Col,
  Radio,
  Select,
  DatePicker,
  Checkbox,
} from "antd";
import dayjs from 'dayjs';

import {
  AddressControl,
  ContactField,
  contribEnum,
  localityEnum,
  Messages,
  Notify,
  openNotification,
  util,
} from '@seims/shared';

import { GlobalOutlined } from "@ant-design/icons";
import RepresentativeList from "../representatives/RepresentativeList";
import partnerAPI from "../../services/partnershipService";
import commonpartnershipLookupAPI  from '../../services/commonLookupService';
import partnershipLookupAPI from "../../services/partnershipLookupService";
import api from "../../services/partnershipService";
import { recordError } from "../../utils/logService";

const EditPartner = () => {
  const [types, setTypes] = useState([]);
  const [titles, setTitle] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [localityOptions, setLocalityOption] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [contactValue, setContactValue] = useState("");
  const [partnerSummary, setPartnerSummary] = useState({
    name: "",
    contact: "",
  });
  const [address, setAddress] = useState({});
  const [isLocal, setIsLocal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddressInfo, setshowAddressInfo] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [reportedOnChecked, setReportedOnChecked] = useState(true);
  const [activeChecked, setActiveChecked] = useState(true);

  const { key } = useParams();
  const location = useLocation();
  let navigate = useNavigate();

  const formItemLayout = {
    labelCol: {
      span: 8,
    },
    wrapperCol: {
      span: 16,
    },
  };

  const { Option } = Select;
  const { Meta } = Card;
  const [form] = Form.useForm();
  const formName = "PartnerForm";

  useEffect(() => {
    getPartnerTypes();
    getLocalityTypes();
    getContributions();
    getTitles();
  }, []);

  useEffect(() => {
    prePopulate();
  }, [contributions, types]);
  
  const prePopulate = async () => {
    let _contributions = [];
    
    if (key) {
      const id = key.substring(key.indexOf("x") + 1, key.length);
      let updatedContact = ""
      const response = await partnerAPI.getPartner(id);

      if (!location.state) {
        openNotification(
          "Partners",
          "System is redirecting to partner list",
          Notify.INFO
        );
        navigate("/partnership", { replace: true });

        return;
      }
      const { state } = location;

      if (response) {
        //FROM SERVER
        const { data } = response;

        //PASSED FROM PARTNER LIST SCREEN
        const {
          // contributions: data.contributions,
          locality,
          partnerType,
          telephoneNumber,
          name,
          email,
          otherContribution,
          initiationDate,
          active,
          reportedOn,
        } = state.data;
        setLocality(locality);
        setshowAddressInfo(true);
        setAddress(data.address);

        try {
          updatedContact = util.validateAndFormatContact(telephoneNumber);
        } catch (error) {
          console.error(error);
        }

        const _partnerType = types.filter(
          (record) => record.key === partnerType.key
        )[0];

        if (data?.contributions !== undefined)
        {
          _contributions = data?.contributions.map((k) => k.toString());
        }

        setContributionOther(_contributions);
        form.setFieldsValue({
          name: name,
          email: email,
          contributions: _contributions,
          address: locality,
          type: _partnerType,
          other: otherContribution,
          initiationDate: dayjs(initiationDate),
        });
        setReportedOnChecked(reportedOn);
        setActiveChecked(active);
        setContactValue(updatedContact.formatted);
        getRepresentatives(data?.representatives);
        setPartnerSummary({ name: name, contact: updatedContact.formatted });
      }
    }
  };

  const onContributeSelection = () => {
    const selectedValues = form.getFieldValue("contributions");
    setContributionOther(selectedValues);
  };

  const setContributionOther = (selectedValues) => {
    const otherContribution = contributions.find(
      (record) => record.value === contribEnum.OTHER
    );

    setShowOther(selectedValues.includes(otherContribution?.key.toString()));
  };
  const getRepresentatives = (representatives) => {
    if (representatives) {
      const updatedRepr = representatives.map((item) => ({
        id: item.id,
        key: item.cellNumber,
        fullname: `${titles.length > 0
          ? titles.find((person) => person.key == item.title).value
          : ""
          } ${item.name} ${item.surname}`,
        title: item.title,
        name: item.name,
        surname: item.surname,
        email: item.email,
        cellNumber: item.cellNumber,
        telephoneNumber: item.telephoneNumber,
        uuid: item.uuid,
      }));
      setRepresentatives(updatedRepr);
    }
  };

  const handleGetRepresentatives = (representatives) => {
    const updatedRepr = representatives.map((item) => ({
      key: item.cellNumber,
      id: item.id,
      uuid: item.uuid,
      title: item.title,
      name: item.name,
      surname: item.surname,
      fullname: item.fullname,
      email: item.email,
      cellNumber: item.cellNumber,
      telephoneNumber: item.telephoneNumber,
    }));

    setRepresentatives(updatedRepr);
  };

  const getPartnerTypes = async () => {
    try {
      const response = await partnershipLookupAPI.getPartnerTypes();
      if (response) {
        const { data } = response;
        setTypes(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getLocalityTypes = async () => {
    try {
      const response = await partnershipLookupAPI.getLocalityTypes();
      if (response) {
        const { data } = response;
        setLocalityOption(data.sort(util.setDynamicSort("key")));
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getContributions = async () => {
    try {
      const module = 'partnership';
      const response = await partnershipLookupAPI.getContributions(module);
      if (response) {
        const { data } = response;
        setContributions(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getTitles = async () => {
    try {
      const response = await commonpartnershipLookupAPI.getTitles();
      if (response) {
        const { data } = response;
        setTitle([...data]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const onChange = (selected) => {
    const selectedType = types.find((record) => record.value === selected);
    form.setFieldsValue({ type: selectedType });
  };

  const onChangeLocality = (e) => {
    const { value: selected } = e.target;
    setLocality(selected);
    setShowModal(true);
  };

  const onInitiationChange = (selectedDate) => { };

  const disabledDates = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const setLocality = (selected) => {
    const selectedLocality = localityOptions?.find(
      (record) => record.key === selected
    );

    if (selectedLocality?.value == localityEnum.LOCAL) {
      setIsLocal(true);
    } else {
      setIsLocal(false);
    }
  };
  const handleCancel = () => {
    setShowModal(false);
  };

  const handleContactChange = (value) => {
    if (value === 0 || value.length < 10) {
      form.setFieldsValue({ contact: undefined });
      return;
    }
    form.setFieldsValue({ contact: value });
  };

  const onNameBlur = (e) => {
    const { value, name } = e.target;
    const textValue = util.toPascalText(value);
    form.setFieldsValue({ [name]: textValue });
  };

  const handleModalSubmit = (values) => {
    let _address = { ...values, country: values.country.value };
    if (isLocal) {
      _address = {
        ...values,
        country: values.country.value,
        province: values.province.value,
      };
    }

    setAddress(_address);
    setShowModal(false);
    setshowAddressInfo(true);
  };

  const onSubmit = async (partner) => {
    const { state } = location;
    const { id } = state.data;
    var action = "Updating Partner";
    var msg;
    const entity = "Partner";

    const contribs = partner.contributions;
    const selectedPartnerType = form.getFieldValue("type");
    if (!util.isEmpty(address)) {
      const _partner = {
        ...partner,
        id: id,
        contributions: contribs,
        partnerType: selectedPartnerType,
        active: activeChecked,
        reportedOn: reportedOnChecked,
      };

      try {
        const request = await api.updatePartnership(
          _partner,
          representatives,
          address
        );

        if (request.status == 200 || request.status == 201) {
          msg = util.parseMessage(Messages.UPDATE_SUCCESS, entity);

          openNotification(action, msg, Notify.SUCCESS);

          navigate("/partnership", { replace: true });
          return;
        }

        msg = util.parseMessage(Messages.UPDATE_FAILURE, entity);
        openNotification(action, msg, Notify.ERROR);
      } catch (error) {
        if (error.response.status == 409) {
          msg = util.parseMessage(Messages.EXIST_UPDATE_FAILURE, entity);
          openNotification(action, msg, Notify.ERROR);
          recordError(error);

          return;
        }

        msg = util.parseMessage(Messages.UPDATE_FAILURE, entity);

        openNotification(action, msg, Notify.ERROR);
      }
    }
  };

  const onChangeCheck = (e) => {
    const { checked, name } = e.target;

    if (name == "active") setActiveChecked(checked);
    else setReportedOnChecked(checked);
  };

  const activeOnlabel = `${activeChecked ? "Yes" : "No!"}`;
  const reportedOnlabel = `${reportedOnChecked ? "Yes" : "No!"}`;

  return (
    <>
      <div className="content">
        {/* <PageHeader
          title="Edit Partner"
          subTitle="Edit partner, add address and more"
          extra={[
            <Link key="1" to="/partnership">
              <Button type="default">Back</Button>
            </Link>,
            <Button key="2" htmlType="submit" form={formName} type="primary">
              Save
            </Button>,
          ]}
        ></PageHeader> */}
        <Row>
          <Col xs={24} md={24} >
            <h1 className="center-align">Edit Partner</h1>
            <br />
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <div className="main">
              <Row>
                <Col xs={24} md={10} >
                  <div className="form-container">
                    <Form
                      {...formItemLayout}
                      name={formName}
                      form={form}
                      layout="horizontal"
                      onFinish={onSubmit}
                    >
                      <Form.Item
                        name="name"
                        label="Name"
                        rules={[
                          { required: true, message: "Please enter partner name" },
                        ]}
                      >
                        <Input name="name" onBlur={onNameBlur} />
                      </Form.Item>
                      <Form.Item
                        name="type"
                        label="Partner Type"
                        rules={[
                          { required: true, message: "Please select partner type" },
                        ]}
                      >
                        <Select onChange={onChange}>
                          {types.map((type) => (
                            <Option key={type.value}>{type.text}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="initiationDate"
                        label="Initiation Date"
                        rules={[
                          { required: true, message: "Please select initiation date" },
                        ]}
                      >
                        <DatePicker
                          onChange={onInitiationChange}
                          picker="month"
                          disabledDate={disabledDates}
                          disabled={true}
                        />
                      </Form.Item>
                      <Form.Item
                        name="contact"
                        label="Contact"
                        rules={[
                          { required: true, message: "Please enter partner contact" },
                          {
                            min: 10,
                            message: "Contact length expects 10 numeric values",
                          },
                        ]}
                      >
                        <ContactField
                          contactValue={contactValue}
                          onContactChange={handleContactChange}
                        />
                      </Form.Item>
                      <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                          {
                            required: true,
                            type: "email",
                            message: "Please enter partner Email",
                          },
                        ]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="address"
                        label="Address Option"
                        rules={[
                          {
                            required: true,
                            message: "Please select partner address option",
                          },
                        ]}
                      >
                        <Radio.Group onChange={onChangeLocality} value={selectedOption}>
                          {localityOptions.map((option) => (
                            <Radio key={option.key} value={option.key}>
                              {option.value}
                            </Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                      <div style={{ marginBottom: 20 }}>
                        {showAddressInfo && (
                          <Card
                            style={{
                              width: "90%",
                              display: "block",
                              margin: "0px 0px 0px auto",
                              marginTop: 16,
                              marginBottom: 10,
                              backgroundColor: "rgb(250 250 250)",
                              borderBlockStyle: "solid",
                              borderColor: "#CCC",
                            }}
                          
                            actions={[
                              <Button
                              style={{
                               
                                display: "block",
                                margin: "0px 10px 0px auto",
                                
                              }}
                                key="Edit"
                                value="Edit"
                                type="primary"
                                onClick={() => setShowModal(true)}
                              >
                                Edit
                              </Button>,
                            ]}
                          >
                            {isLocal ? (
                              <Meta
                                avatar={<Avatar icon={<GlobalOutlined />} />}
                                title={"Local Address"}
                                description={`${address.street} ${address.cityOrTown} ${address.province} ${address.postalCode}, ${address.country}`}
                              />
                            ) : (
                              <Meta
                                avatar={<Avatar icon={<GlobalOutlined />} />}
                                title={"Foreign Address"}
                                description={`${address.street} ${address.cityOrTown} ${address.province} ${address.postalCode}, ${address.country}`}
                              />
                            )}
                          </Card>
                        )}
                      </div>
                      <Form.Item
                        name="contributions"
                        label="Contributions"
                        rules={[
                          {
                            required: true,
                            message: "Please select at least 1 contribution type",
                          },
                        ]}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="Please select"
                          onSelect={onContributeSelection}
                          onChange={onContributeSelection}
                        >
                          {contributions.map((contribute) => (
                            <Option key={contribute.key}>{contribute.value}</Option>
                          ))}
                        </Select>
                      </Form.Item>

                      {showOther && (
                        <Form.Item
                          name="other"
                          label="Other"
                          rules={[
                            {
                              required: true,
                              message: "Please enter other contibutions",
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      )}
                      <Form.Item name="active" label="Is Active?">
                        <Checkbox
                          name="active"
                          checked={activeChecked}
                          onChange={onChangeCheck}
                        >
                          {activeOnlabel}
                        </Checkbox>
                      </Form.Item>
                      <Form.Item name="reportedOn" label="Is Reported On?">
                        <Checkbox
                          name="reportedOn"
                          checked={reportedOnChecked}
                          onChange={onChangeCheck}
                        >
                          {reportedOnlabel}
                        </Checkbox>
                      </Form.Item>
                    </Form>
                  </div>
                </Col>
                <Col md={2}></Col>
                <Col xs={24} md={12} >
                  <div style={{ marginBottom: 20 }}>
                    {representatives && (
                      <RepresentativeList
                        key="1"
                        isAdd={false}
                        partnerSummary={partnerSummary}
                        repList={representatives}
                        onGetAll={handleGetRepresentatives}
                      />
                    )}
                    {address && (
                      <AddressControl
                        key="2"
                        onCancel={handleCancel}
                        visible={showModal}
                        title="Edit Address"
                        isLocal={isLocal}
                        onModalSubmit={handleModalSubmit}
                        address={address}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={24} className="right-align">
            <Link key="1" to="/partnership">
              <Button type="default" style={{ marginRight: 10 }}>Back</Button>
            </Link>,
            <Button key="2" htmlType="submit" form={formName} type="primary">
              Save
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default EditPartner;
